import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Divider,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import BuildingIcon from '@mui/icons-material/Apartment';
import { login } from '../features/auth/authSlice';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [visitorOTP, setVisitorOTP] = useState('');
  const [userType, setUserType] = useState('resident');
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated, userRole } = useSelector(state => state.auth);
  
  // 如果已登入，重定向到相應的儀表板
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || getDashboardPath(userRole);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, userRole, navigate, location]);
  
  // 根據用戶角色獲取適當的儀表板路徑
  const getDashboardPath = (role) => {
    switch (role) {
      case 'resident':
        return '/resident';
      case 'tenant':
        return '/tenant';
      case 'security':
        return '/security';
      case 'manager':
        return '/manager';
      case 'visitor':
        return '/visitor';
      default:
        return '/';
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleLogin = (event) => {
    event.preventDefault();
    
    if (tabValue === 0) {
      // 一般用戶登入
      if (!username.trim() || !password.trim()) {
        return;
      }
      
      dispatch(login({ username, password, userType }));
    } else {
      // 訪客OTP登入
      if (!visitorOTP.trim()) {
        return;
      }
      
      dispatch(login({ otp: visitorOTP, userType: 'visitor' }));
    }
  };
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/logo192.png"
          sx={{
            width: 80,
            height: 80,
            mb: 2
          }}
          alt="智能大廈"
        />
        
        <Typography component="h1" variant="h4" gutterBottom>
          智能大廈管理系統
        </Typography>
        
        <Paper 
          elevation={3} 
          sx={{ 
            width: '100%', 
            mt: 3, 
            borderRadius: 2, 
            overflow: 'hidden' 
          }}
        >
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="用戶登入" />
            <Tab label="訪客通行" />
          </Tabs>
          
          <Box component="form" onSubmit={handleLogin} noValidate sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {tabValue === 0 && (
              // 一般用戶登入表單
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="user-type-label">用戶類型</InputLabel>
                  <Select
                    labelId="user-type-label"
                    value={userType}
                    label="用戶類型"
                    onChange={(e) => setUserType(e.target.value)}
                  >
                    <MenuItem value="resident">住戶</MenuItem>
                    <MenuItem value="tenant">租戶</MenuItem>
                    <MenuItem value="security">保安員</MenuItem>
                    <MenuItem value="manager">物業管理員</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="用戶名稱"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="密碼"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Grid container sx={{ mt: 2, mb: 2 }}>
                  <Grid item xs>
                    <Link href="#" variant="body2">
                      忘記密碼?
                    </Link>
                  </Grid>
                </Grid>
              </>
            )}
            
            {tabValue === 1 && (
              // 訪客通行表單
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2, textAlign: 'center' }}>
                  請輸入住戶或租戶提供的一次性密碼
                </Typography>
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="visitorOTP"
                  label="訪客通行碼"
                  name="visitorOTP"
                  autoFocus
                  value={visitorOTP}
                  onChange={(e) => setVisitorOTP(e.target.value)}
                  placeholder="例如: XYZ123"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BuildingIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  通行碼僅在指定時間內有效一次
                </Typography>
              </>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : tabValue === 0 ? '登入' : '訪客通行'}
            </Button>
            
            {tabValue === 0 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  如有疑問，請聯絡管理處
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage; 